import { useEffect, useState } from "react";
import type { SettingsViewId } from "../settingsLandingData";
import {
  ContractCrewsResponse,
  getContractCrews,
} from "../../../services/ContractCrewsService";
import { getContractMachines } from "../../../services/ContractMachinesService";
import { ContractMachinesResponse } from "../../../types/ContractMachine";
import {
  ContractPriceGroupsResponse,
  getContractPriceGroups,
} from "../../../services/ContractPriceGroupsService";
import {
  ContractSidecostGroupsResponse,
  getContractSidecostGroups,
} from "../../../services/ContractSidecostGroupsService";
import {
  GeneralPriceListResponse,
  getGeneralPriceList,
} from "../../../services/GeneralPriceListService";
import {
  GeneralSidecostGroupsResponse,
  getGeneralSidecostGroups,
} from "../../../services/GeneralSidecostGroupsService";
import { DeputiesResponse, getDeputies } from "../../../services/DeputiesService";
import { Subscriber, getSubscribers } from "../../../services/SubscribersService";

type CheckAccess = (functionCode: string) => boolean;
type IsViewVisible = (viewId: SettingsViewId) => boolean;

export function useSettingsPrefetch(
  selectedAddressSystemId: number,
  checkAccess: CheckAccess,
  isViewVisible: IsViewVisible,
) {
  const [prefetchedPriceList, setPrefetchedPriceList] =
    useState<GeneralPriceListResponse | null>(null);
  const [prefetchedSubscribers, setPrefetchedSubscribers] = useState<
    Subscriber[] | null
  >(null);
  const [prefetchedDeputies, setPrefetchedDeputies] =
    useState<DeputiesResponse | null>(null);
  const [prefetchedContractPriceGroups, setPrefetchedContractPriceGroups] =
    useState<ContractPriceGroupsResponse | null>(null);
  const [prefetchedContractSidecostGroups, setPrefetchedContractSidecostGroups] =
    useState<ContractSidecostGroupsResponse | null>(null);
  const [prefetchedGeneralSidecostGroups, setPrefetchedGeneralSidecostGroups] =
    useState<GeneralSidecostGroupsResponse | null>(null);
  const [prefetchedContractCrews, setPrefetchedContractCrews] =
    useState<ContractCrewsResponse | null>(null);
  const [prefetchedContractMachines, setPrefetchedContractMachines] =
    useState<ContractMachinesResponse | null>(null);

  const resetContractScopedPrefetch = (view: SettingsViewId | null) => {
    if (view === "deputies") {
      setPrefetchedDeputies((current) =>
        current
          ? {
              ...current,
              selectedContractId: null,
              archiveEmail: "",
              contractorArchiveEmail: "",
              roleGroups: [],
            }
          : current,
      );
    }

    if (view === "contract-crews") {
      setPrefetchedContractCrews((current) =>
        current
          ? {
              ...current,
              selectedContractId: null,
              crews: [],
            }
          : current,
      );
    }

    if (view === "contract-machines") {
      setPrefetchedContractMachines((current) =>
        current
          ? {
              ...current,
              selectedContractId: null,
              machines: [],
              showCopyFromTemplate: false,
            }
          : current,
      );
    }

    if (view === "contract-sidecost-groups") {
      setPrefetchedContractSidecostGroups((current) =>
        current
          ? {
              ...current,
              selectedContractId: null,
              groups: [],
              hasAnySidecostItems: false,
            }
          : current,
      );
    }
  };

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("ADMIN_PRICES")) {
      setPrefetchedPriceList(null);
      return undefined;
    }

    if (!isViewVisible("unit-price-list-lines")) {
      setPrefetchedPriceList(null);
      return undefined;
    }

    let cancelled = false;
    getGeneralPriceList(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedPriceList(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch unit price list lines", error);
          setPrefetchedPriceList(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("SIVUKULUT_YLLAPITO")) {
      setPrefetchedContractSidecostGroups(null);
      return undefined;
    }

    if (!isViewVisible("contract-sidecost-groups")) {
      setPrefetchedContractSidecostGroups(null);
      return undefined;
    }

    let cancelled = false;
    getContractSidecostGroups(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedContractSidecostGroups(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch contract indirect costs", error);
          setPrefetchedContractSidecostGroups(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("SIVUKULUT_YLLAPITO")) {
      setPrefetchedGeneralSidecostGroups(null);
      return undefined;
    }

    if (!isViewVisible("general-sidecost-groups")) {
      setPrefetchedGeneralSidecostGroups(null);
      return undefined;
    }

    let cancelled = false;
    getGeneralSidecostGroups(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedGeneralSidecostGroups(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch general indirect cost groups", error);
          setPrefetchedGeneralSidecostGroups(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("ADMIN_PRICES")) {
      setPrefetchedContractPriceGroups(null);
      return undefined;
    }

    if (!isViewVisible("unit-prices-for-contracts")) {
      setPrefetchedContractPriceGroups(null);
      return undefined;
    }

    let cancelled = false;
    getContractPriceGroups(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedContractPriceGroups(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch contract unit prices", error);
          setPrefetchedContractPriceGroups(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("ADMIN_PRICES")) {
      setPrefetchedContractCrews(null);
      return undefined;
    }

    if (!isViewVisible("contract-crews")) {
      setPrefetchedContractCrews(null);
      return undefined;
    }

    let cancelled = false;
    getContractCrews(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedContractCrews(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch working groups", error);
          setPrefetchedContractCrews(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("ADMIN_PRICES")) {
      setPrefetchedContractMachines(null);
      return undefined;
    }

    if (!isViewVisible("contract-machines")) {
      setPrefetchedContractMachines(null);
      return undefined;
    }

    let cancelled = false;
    getContractMachines(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedContractMachines(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch equipment", error);
          setPrefetchedContractMachines(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (selectedAddressSystemId <= 0 || !checkAccess("ADMIN_PRICES")) {
      setPrefetchedSubscribers(null);
      return undefined;
    }

    if (!isViewVisible("subscribers")) {
      setPrefetchedSubscribers(null);
      return undefined;
    }

    let cancelled = false;
    void getSubscribers(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedSubscribers(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch subscribers", error);
          setPrefetchedSubscribers(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  useEffect(() => {
    if (
      selectedAddressSystemId <= 0 ||
      !checkAccess("CITY_OBJECT_DEPUTY_MANAGEMENT")
    ) {
      setPrefetchedDeputies(null);
      return undefined;
    }

    if (!isViewVisible("deputies")) {
      setPrefetchedDeputies(null);
      return undefined;
    }

    let cancelled = false;
    void getDeputies(selectedAddressSystemId)
      .then((result) => {
        if (!cancelled) {
          setPrefetchedDeputies(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch deputies", error);
          setPrefetchedDeputies(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAddressSystemId, checkAccess, isViewVisible]);

  return {
    prefetchedPriceList,
    setPrefetchedPriceList,
    prefetchedSubscribers,
    setPrefetchedSubscribers,
    prefetchedDeputies,
    setPrefetchedDeputies,
    prefetchedContractPriceGroups,
    setPrefetchedContractPriceGroups,
    prefetchedContractSidecostGroups,
    setPrefetchedContractSidecostGroups,
    prefetchedGeneralSidecostGroups,
    setPrefetchedGeneralSidecostGroups,
    prefetchedContractCrews,
    setPrefetchedContractCrews,
    prefetchedContractMachines,
    setPrefetchedContractMachines,
    resetContractScopedPrefetch,
  };
}
